// ProfilePhotoUploader.tsx
import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import styles from './ProfilePhotoUploader.module.scss';
import profilePlaceholder from '../assets/profile-placeholder.jpg';

interface Props {
  userId: string;
}

export default function ProfilePhotoUploader({ userId }: Props) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) fetchCurrentProfilePicture();
  }, [userId]);

  const fetchCurrentProfilePicture = async () => {
    try {
      const res = await api.post('/core/files/get', {
        user_id: userId,
        file_for: 'user_profile_picture',
      });
      if (res.data?.files?.length > 0 && res.data.files[0].signed_url) {
        setPreviewUrl(res.data.files[0].signed_url); // Use signed_url
      } else {
        setPreviewUrl(profilePlaceholder);
      }
    } catch (err) {
      console.error('Profile picture fetch failed:', err);
      setError('Failed to load profile picture');
      setPreviewUrl(profilePlaceholder);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!userId) {
      setError('User ID is missing');
      console.error('Missing auth data:', { userId });
      return;
    }

    setFile(selected);
    setUploading(true);
    setError(null);

    console.log('Uploading with:', { user_id: userId, file_for: 'user_profile_picture' });

    const tempUrl = URL.createObjectURL(selected);
    setPreviewUrl(tempUrl);

    const formData = new FormData();
    formData.append('file', selected);
    formData.append('user_id', userId);
    formData.append('file_for', 'user_profile_picture');

    try {
      const uploadRes = await api.post('/core/files/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (uploadRes.data?.file_path && uploadRes.data?.file?.signed_url) {
        setPreviewUrl(uploadRes.data.file.signed_url); // Use signed_url from create response
      } else {
        await fetchCurrentProfilePicture(); // Fallback to fetch current picture
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError('Failed to upload profile picture: ' + (err.response?.data?.message || err.message));
      await fetchCurrentProfilePicture();
    } finally {
      setUploading(false);
      URL.revokeObjectURL(tempUrl);
    }
  };

  return (
    <div className={styles.wrapper}>
      <label className={styles.avatar}>
        <img
          src={previewUrl || profilePlaceholder}
          alt="Profile"
          className={uploading ? styles.uploading : ''}
        />
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
        />
        {uploading && <div className={styles.loader}>Uploading...</div>}
      </label>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}